import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScanLine, CheckCircle2, XCircle, Sprout } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Checkin() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [sessionInfo, setSessionInfo] = useState(null);
  const [error, setError] = useState(null);
  const [matricola, setMatricola] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!token) { setError("QR non valido — token mancante"); return; }
    fetch(`${BACKEND_URL}/api/attendance/qr-info?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(j.detail || "QR scaduto o non valido");
        }
        return r.json();
      })
      .then(setSessionInfo)
      .catch((e) => setError(e.message));
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true); setError(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/attendance/qr-checkin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, matricola: matricola.trim() }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Errore check-in");
      }
      const data = await r.json();
      setSuccess(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain-bg flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-[#1C352D] grid place-items-center text-white"><Sprout size={20}/></div>
          <div>
            <div className="font-semibold text-[#1C352D]">RDM ClassRoom</div>
            <div className="text-[11px] text-stone-500 uppercase tracking-widest">Check-in presenza</div>
          </div>
        </div>

        {success ? (
          <Card className="p-8 border border-emerald-200 bg-emerald-50 rounded-2xl shadow-none text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-600 text-white grid place-items-center mx-auto"><CheckCircle2 size={28}/></div>
            <h1 className="text-2xl font-semibold text-emerald-900 mt-4">Presenza registrata!</h1>
            <div className="text-sm text-emerald-800 mt-2">
              {success.student_name} · <span className="mono">{success.matricola}</span>
            </div>
            <div className="text-xs text-emerald-700 mt-1">
              {success.session_title} · ingresso <span className="mono">{success.arrival_time}</span>
            </div>
            <div className="text-[11px] text-emerald-700/70 mt-6">Ora puoi chiudere questa pagina.</div>
          </Card>
        ) : error && !sessionInfo ? (
          <Card className="p-8 border border-red-200 bg-red-50 rounded-2xl shadow-none text-center">
            <div className="w-14 h-14 rounded-full bg-red-600 text-white grid place-items-center mx-auto"><XCircle size={28}/></div>
            <h1 className="text-xl font-semibold text-red-900 mt-4">{error}</h1>
            <div className="text-sm text-red-800 mt-2">
              Chiedi al docente il QR aggiornato e riprova.
            </div>
          </Card>
        ) : sessionInfo ? (
          <Card className="p-8 border border-stone-200 rounded-2xl shadow-none">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-stone-500">
              <ScanLine size={12}/> Sessione confermata
            </div>
            <h1 className="text-2xl font-semibold text-[#1C352D] mt-1">{sessionInfo.title}</h1>
            <div className="text-xs text-stone-500 mt-1">
              {sessionInfo.date}{sessionInfo.year_level ? ` · Aula ${["I","II","III"][sessionInfo.year_level-1]} anno` : ""}
            </div>

            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">La tua matricola</Label>
                <Input
                  data-testid="checkin-matricola"
                  value={matricola}
                  onChange={(e) => setMatricola(e.target.value.toUpperCase())}
                  placeholder="STU-2026-0001"
                  className="mt-1.5 mono uppercase text-lg tracking-wider"
                  autoFocus required
                />
              </div>
              {error && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                data-testid="checkin-submit"
                disabled={!matricola || submitting}
                className="w-full h-12 rounded-full bg-[#1C352D] hover:bg-[#264A3E] transition-colors duration-150 text-base"
              >
                {submitting ? "Registrazione…" : "Segna presente"}
              </Button>
            </form>
            <div className="text-[11px] text-stone-400 mt-6 text-center">
              Se il QR è scaduto, chiedi al docente di mostrare quello nuovo.
            </div>
          </Card>
        ) : (
          <Card className="p-10 text-center text-stone-500 border border-stone-200 rounded-2xl shadow-none">Verifica QR…</Card>
        )}

        <div className="text-center mt-6">
          <Link to="/" className="text-[11px] text-stone-400 hover:text-[#1C352D]">Torna all&apos;app</Link>
        </div>
      </div>
    </div>
  );
}
